import { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import useFundAccess, { fundPeopleUrl, myFundAccessUrl } from '@/hooks/useFundAccess';
import { putAuth } from '@/api/ApiCalls';
import { IsraeliIdDialog } from '@/pages/FundSettings/people/PeopleDialogs';

const laterKey = (email: string | undefined) => `fundPeople.israeliIdLaterOn.${(email ?? '').toLowerCase()}`;

/** Today in the reader's own calendar - «אחר כך» holds until the date changes. */
const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
};

const readLater = (email: string | undefined) => {
  try {
    return localStorage.getItem(laterKey(email));
  } catch {
    return null;
  }
};

const writeLater = (email: string | undefined) => {
  try {
    localStorage.setItem(laterKey(email), today());
  } catch {
    // Without storage the window simply asks again on the next entry.
  }
};

/**
 * Every coordinator is identified by an Israeli ID (Druk, 22.10.2026). One who has none yet - no
 * representative card in the fund and no number on the account - is asked for it on entering the
 * fund. Not a wall (Chaya, 1.11.2026): «אחר כך» lets them work as before, and the window asks again
 * the next day. What needs the number still waits for it - a lead coordinator's role, adding a
 * coordinator. Saving it through «החשבון שלי»'s endpoint also makes them a representative of their
 * funds on the server. Only with «רכזים והרשאות» on: the access call is asked only then.
 */
const IsraeliIdGate = ({ fundId, fundName }: { fundId: number; fundName: string }) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const queryClient = useQueryClient();
  const { access } = useFundAccess(fundId);
  const [putOff, setPutOff] = useState(() => readLater(currentUser?.email) === today());

  const save = useMutation({
    mutationFn: (israeliId: string) =>
      putAuth<unknown, object, { israeliId: string }>('users/israeli-id', { Authorization: `Bearer ${currentUser?.token}` }, {}, { israeliId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users/get'] });
      queryClient.invalidateQueries({ queryKey: [myFundAccessUrl(fundId)] });
      queryClient.invalidateQueries({ queryKey: [fundPeopleUrl(fundId)] });
    },
  });

  if (!access?.needsIsraeliId || access.isOffice || putOff) return null;

  const error = !save.isError
    ? null
    : axios.isAxiosError(save.error) && save.error.response?.status === 409
      ? t('fundPeople.idDialog.taken')
      : axios.isAxiosError(save.error) && typeof save.error.response?.data === 'string' && save.error.response.data.trim()
        ? save.error.response.data
        : t('fundPeople.genericError');

  return (
    <IsraeliIdDialog
      open
      asGate
      onClose={() => {
        writeLater(currentUser?.email);
        setPutOff(true);
      }}
      onSubmit={(israeliId) => save.mutate(israeliId)}
      fundLabel={`${fundId} · ${fundName}`}
      isPending={save.isPending}
      error={error}
    />
  );
};

export default IsraeliIdGate;
